import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/contexts/AppContext";
import { toast } from "sonner";
import { CreditCard, Building2, TrendingUp, RefreshCw, Download } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};

const StatusBadge = ({ status }) => {
  const map = {
    active: { bg: "#2E7D3215", color: "#2E7D32", label: "Actif" },
    COMPLETED: { bg: "#2E7D3215", color: "#2E7D32", label: "Payé" },
    PENDING: { bg: "#E3A72F15", color: "#E3A72F", label: "En attente" },
    FAILED: { bg: "#C6282815", color: "#C62828", label: "Échoué" },
    expired: { bg: "#5C6B6115", color: "#5C6B61", label: "Expiré" },
    ACCEPTED: { bg: "#1E463115", color: "#1E4631", label: "Accepté" },
    SUBMITTED: { bg: "#E3A72F15", color: "#E3A72F", label: "Soumis" },
    pending: { bg: "#E3A72F15", color: "#E3A72F", label: "En attente" },
    cancelled: { bg: "#5C6B6115", color: "#5C6B61", label: "Annulé" },
  };
  const s = map[status] || { bg: "#5C6B6115", color: "#5C6B61", label: status || "—" };
  return (
    <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded" style={{ background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
};

export default function SuperSubscriptions() {
  const [subs, setSubs] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backupBusy, setBackupBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/subscriptions/all`);
      setSubs(r.data.subscriptions || []);
      setPayments(r.data.recent_payments || []);
    } catch { toast.error("Impossible de charger les abonnements"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const triggerBackup = async () => {
    setBackupBusy(true);
    try {
      await axios.post(`${API}/backup/run-now`);
      toast.success("Sauvegarde CSV envoyée par email à tous les hôpitaux actifs");
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec du backup"); }
    finally { setBackupBusy(false); }
  };

  const activeSubs = subs.filter((s) => s.status === "active");
  const mrrUSD = activeSubs.reduce((sum, s) => {
    const amt = parseFloat(s.amount || "0");
    return sum + (s.currency === "USD" ? amt : amt / 2750);  // rough CDF→USD
  }, 0);
  const completedCount = payments.filter((p) => p.status === "COMPLETED").length;

  return (
    <div className="space-y-6" data-testid="super-subscriptions-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-[Outfit] font-bold flex items-center gap-3">
            <CreditCard className="w-7 h-7 text-[#1E4631]"/> Abonnements & Paiements
          </h1>
          <p className="text-[#5C6B61] mt-1 text-sm">Vue globale de tous les hôpitaux payants sur la plateforme.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} data-testid="refresh-subs-btn"
            className="px-4 py-2 rounded-xl border border-[#E5EBE7] bg-white hover:bg-[#FDFBF7] text-sm font-medium inline-flex items-center gap-2">
            <RefreshCw className="w-4 h-4"/> Actualiser
          </button>
          <button onClick={triggerBackup} disabled={backupBusy} data-testid="run-backup-btn"
            className="px-4 py-2 rounded-xl bg-[#1E4631] hover:bg-[#153224] text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60">
            <Download className="w-4 h-4"/> {backupBusy ? "Envoi…" : "Lancer backup CSV"}
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl border border-[#E5EBE7] p-5" data-testid="kpi-active-subs">
          <div className="w-10 h-10 rounded-xl grid place-items-center bg-[#2E7D32]/15 text-[#2E7D32]"><Building2 className="w-5 h-5"/></div>
          <div className="mt-3 text-2xl font-[Outfit] font-bold">{activeSubs.length}</div>
          <div className="text-xs text-[#5C6B61] mt-0.5">Abonnements actifs</div>
        </div>
        <div className="bg-white rounded-2xl border border-[#E5EBE7] p-5" data-testid="kpi-mrr-real">
          <div className="w-10 h-10 rounded-xl grid place-items-center bg-[#1E4631]/15 text-[#1E4631]"><TrendingUp className="w-5 h-5"/></div>
          <div className="mt-3 text-2xl font-[Outfit] font-bold">${mrrUSD.toFixed(0)}</div>
          <div className="text-xs text-[#5C6B61] mt-0.5">MRR réel (USD/mois)</div>
        </div>
        <div className="bg-white rounded-2xl border border-[#E5EBE7] p-5" data-testid="kpi-payments-done">
          <div className="w-10 h-10 rounded-xl grid place-items-center bg-[#D96C4A]/15 text-[#D96C4A]"><CreditCard className="w-5 h-5"/></div>
          <div className="mt-3 text-2xl font-[Outfit] font-bold">{completedCount}</div>
          <div className="text-xs text-[#5C6B61] mt-0.5">Paiements réussis (récents)</div>
        </div>
        <div className="bg-white rounded-2xl border border-[#E5EBE7] p-5" data-testid="kpi-total-payments">
          <div className="w-10 h-10 rounded-xl grid place-items-center bg-[#E3A72F]/15 text-[#E3A72F]"><CreditCard className="w-5 h-5"/></div>
          <div className="mt-3 text-2xl font-[Outfit] font-bold">{payments.length}</div>
          <div className="text-xs text-[#5C6B61] mt-0.5">Transactions récentes</div>
        </div>
      </div>

      {/* Subscriptions table */}
      <div className="bg-white rounded-2xl border border-[#E5EBE7] p-6">
        <h3 className="font-[Outfit] font-semibold mb-4">Abonnements ({subs.length})</h3>
        {loading ? <div className="text-sm text-[#5C6B61]">Chargement…</div> :
         subs.length === 0 ? <div className="text-sm text-[#5C6B61]">Aucun abonnement enregistré pour l&apos;instant.</div> :
         <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-[#5C6B61] uppercase tracking-wide border-b border-[#E5EBE7]">
              <tr>
                <th className="text-left py-2 pr-3">Hôpital</th>
                <th className="text-left py-2 pr-3">Plan</th>
                <th className="text-left py-2 pr-3">Statut</th>
                <th className="text-right py-2 pr-3">Montant</th>
                <th className="text-left py-2 pr-3">Début</th>
                <th className="text-left py-2 pr-3">Fin de période</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5EBE7]" data-testid="subs-tbody">
              {subs.map((s) => (
                <tr key={s.id}>
                  <td className="py-2 pr-3 font-medium text-[#1A1D1A]">{s.hospital_name}</td>
                  <td className="py-2 pr-3">{s.plan_name}</td>
                  <td className="py-2 pr-3"><StatusBadge status={s.status}/></td>
                  <td className="py-2 pr-3 text-right font-mono">{s.currency} {s.amount}</td>
                  <td className="py-2 pr-3">{fmtDate(s.current_period_start)}</td>
                  <td className="py-2 pr-3">{fmtDate(s.current_period_end)}</td>
                </tr>
              ))}
            </tbody>
          </table>
         </div>
        }
      </div>

      {/* Payments table */}
      <div className="bg-white rounded-2xl border border-[#E5EBE7] p-6">
        <h3 className="font-[Outfit] font-semibold mb-4">Paiements récents ({payments.length})</h3>
        {payments.length === 0 ? <div className="text-sm text-[#5C6B61]">Aucun paiement enregistré.</div> :
         <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-[#5C6B61] uppercase tracking-wide border-b border-[#E5EBE7]">
              <tr>
                <th className="text-left py-2 pr-3">Hôpital</th>
                <th className="text-left py-2 pr-3">Plan</th>
                <th className="text-left py-2 pr-3">Opérateur</th>
                <th className="text-left py-2 pr-3">Statut</th>
                <th className="text-right py-2 pr-3">Montant</th>
                <th className="text-left py-2 pr-3">Passerelle</th>
                <th className="text-left py-2 pr-3">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5EBE7]" data-testid="payments-tbody">
              {payments.map((p) => (
                <tr key={p.id}>
                  <td className="py-2 pr-3 font-medium text-[#1A1D1A]">{p.hospital_name}</td>
                  <td className="py-2 pr-3">{p.plan_name}</td>
                  <td className="py-2 pr-3 text-xs">{p.provider}</td>
                  <td className="py-2 pr-3"><StatusBadge status={p.status}/></td>
                  <td className="py-2 pr-3 text-right font-mono">{p.currency} {p.amount}</td>
                  <td className="py-2 pr-3">
                    <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${
                      p.gateway === "pawapay" ? "bg-[#1E4631]/10 text-[#1E4631]" : "bg-[#E3A72F]/10 text-[#E3A72F]"
                    }`}>{p.gateway}</span>
                  </td>
                  <td className="py-2 pr-3 text-xs">{fmtDate(p.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
         </div>
        }
      </div>
    </div>
  );
}
